import { useCallback, useEffect, useRef } from 'react';
import { Keyboard, LayoutChangeEvent } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { useKeyboardHeight } from '@/hooks/use-keyboard-height';
import { isAndroid } from '@/utils/constants-platform-specific';

import { useSbkBetSlipStore } from './use-sbk-betslip-store';

const CARD_MARGIN_BOTTOM = 16; // matches SinglesCard mb="s16"
const CONTENT_PADDING_TOP = 24; // matches betSlipTabStyle.root paddingVertical
// The BetSlipActionTray is absolutely positioned over the FlatList, so onLayout
// reports a height that includes the area hidden behind the tray. This adjustment
// compensates for that overlap. It can't be derived from constants because the
// tray's rendered height is dynamic (safe area insets, optional summary line).
const SCROLL_POSITION_ADJUSTMENT = isAndroid ? 64 : 88;

/**
 * Handles scrolling the FlatList so the focused bet card's bottom edge
 * sits just above the keyboard. Listens for keyboard show events and
 * editBetId changes to trigger scrolling at the right time.
 */
export const useScrollToEditBet = (flatListRef: React.RefObject<FlatList<string> | null>, enabled = true) => {
    const editBetId = useSbkBetSlipStore(state => (enabled ? state.editBetId : null));
    const selectionIds = useSbkBetSlipStore(state => state.selectionOrder);
    const keyboardHeight = useKeyboardHeight(isAndroid);

    const itemHeightsRef = useRef<Record<string, number>>({});
    const listHeightRef = useRef(0);
    const keyboardHeightRef = useRef(keyboardHeight);
    keyboardHeightRef.current = keyboardHeight;
    const editBetIdRef = useRef(editBetId);
    editBetIdRef.current = editBetId;
    const selectionIdsRef = useRef(selectionIds);
    selectionIdsRef.current = selectionIds;

    const handleItemLayout = useCallback((selectionId: string, event: LayoutChangeEvent) => {
        itemHeightsRef.current[selectionId] = event.nativeEvent.layout.height;
    }, []);

    const handleListLayout = useCallback((event: LayoutChangeEvent) => {
        listHeightRef.current = event.nativeEvent.layout.height;
    }, []);

    const scrollToEditBet = useCallback(() => {
        const id = editBetIdRef.current;
        if (!id) {
            return;
        }
        const ids = selectionIdsRef.current;
        const index = ids.indexOf(id);
        if (index < 0) {
            return;
        }
        const listHeight = listHeightRef.current;
        if (listHeight === 0) {
            return;
        }

        // On Android, adjustResize shrinks the window but the FlatList's onLayout
        // may not have fired yet with the new height. Subtract keyboard height to
        // get the actual visible area. On iOS, KAV shrinks the FlatList directly.
        const kbHeight = keyboardHeightRef.current;
        const visibleHeight = isAndroid && kbHeight > 0 ? listHeight - kbHeight : listHeight;

        // Sum heights of all items up to and including the focused item.
        // onLayout on the Box doesn't include mb="s16", so add margin per item.
        let cardBottomY = CONTENT_PADDING_TOP;
        for (let i = 0; i <= index; i++) {
            cardBottomY += (itemHeightsRef.current[ids[i]] ?? 0) + CARD_MARGIN_BOTTOM;
        }
        cardBottomY -= CARD_MARGIN_BOTTOM;

        const targetOffset = cardBottomY - visibleHeight + SCROLL_POSITION_ADJUSTMENT;

        flatListRef.current?.scrollToOffset({
            offset: Math.max(0, targetOffset),
            animated: true,
        });
    }, [flatListRef]);

    // Trigger scroll when the keyboard finishes showing (layout is settled)
    useEffect(() => {
        const sub = Keyboard.addListener('keyboardDidShow', scrollToEditBet);
        return () => sub.remove();
    }, [scrollToEditBet]);

    // Also trigger scroll when editBetId changes while the keyboard is already visible
    useEffect(() => {
        if (!editBetId) {
            return;
        }
        if (keyboardHeightRef.current > 0) {
            const rafId = requestAnimationFrame(scrollToEditBet);
            return () => cancelAnimationFrame(rafId);
        }
    }, [editBetId, scrollToEditBet]);

    return { handleItemLayout, handleListLayout, keyboardHeight };
};
